import { useCallback, useMemo } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'

import { formatDashSeparator } from 'LuxKit/intl/separators'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Caption from 'LuxKit/Typography/Caption'
import Group from 'components/utils/Group'
import styled from 'styled-components'
import { rem } from 'polished'
import type { TripDay, TripItineraryItem } from 'tripPlanner/api/types'
import { getLocalisedDayLabel } from 'tripPlanner/utils/chatDates'
import TextButton from 'LuxKit/Button/TextButton'
import Clickable from 'components/Common/Clickable'
import noop from 'lib/function/noop'
import ChatItemDropdown from './ChatItemDropdown'
import AssistChip from 'LuxKit/Chips/AssistChip'
import AISparkleIcon from 'components/Search/SearchForm/AISparkleIcon'

interface Props {
  day: TripDay
  items: Array<TripItineraryItem>
  onViewDayItinerary?: (dayId: number) => void
  onDeleteDay?: (dayId: number) => void
  onHighlightClick?: (googlePlaceId: string, placeName: string, itemId: number) => void
  onHotelHighlightClick?: (item: TripItineraryItem) => void
  onAskAI?: (dayNumber: number, dayTitle: string, date?: string) => void
}

const DayCardContainer = styled.div`
  width: 100%;
`
const StyledTextButton = styled(TextButton)`
  padding-left: 0;
`

const AskAiChip = styled(AssistChip)`
  position: relative;
  box-shadow: none;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    padding: ${rem(2)};
    background: linear-gradient(  
      135deg,
      ${props => props.theme.palette.product.ai.light} 0%,
      ${props => props.theme.palette.product.ai.dark} 50%,
      ${props => props.theme.palette.product.ai.light} 100%
    );
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
  }
`

const ClickableHighlightTitle = styled(Clickable)`
  font-weight: bold;
  &:hover {
    text-decoration: underline;
  }
`

function ItineraryContent(props: Props) {
  const { day, items, onViewDayItinerary = noop, onDeleteDay = noop, onHighlightClick = noop, onHotelHighlightClick = noop, onAskAI = noop } = props
  const intl = useIntl()

  const dayLabel = getLocalisedDayLabel(intl, day.dayNumber, day.date)

  const askAILabel = intl.formatMessage({
    id: 'LMRNbMot5T2ZYHczoJwD',
    defaultMessage: 'Ask AI',
    description: 'Chip label to ask AI for suggestions about a trip day',
  })

  const highlightsHeading = intl.formatMessage({
    id: '5tg1QSJOEHgDYoMS4MNF',
    defaultMessage: 'Highlights',
    description: 'Heading for the highlights section of a trip day itinerary',
  })

  const emptyDayMessage = intl.formatMessage({
    id: 'gSkaW0y1lUiW_IYVyftV',
    defaultMessage: 'No items planned for this day yet.',
    description: 'Empty state message when a trip day has no items planned',
  })

  const viewEntireDayLabel = intl.formatMessage({
    id: 'Zt_h3AQbWZtirYTaORhy',
    defaultMessage: 'View entire day',
    description: 'Button label to view the full day itinerary in the itinerary',
  })

  const handleViewDayItineraryClick = useCallback(() => {
    onViewDayItinerary(day.id)
  }, [onViewDayItinerary, day.id])

  const handleDelete = useCallback(() => {
    onDeleteDay(day.id)
  }, [day.id, onDeleteDay])

  const handleAskAIClick = useCallback(() => {
    onAskAI(day.dayNumber, day.title, day.date)
  }, [onAskAI, day.dayNumber, day.title, day.date])

  const handleHighlightClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    const { googlePlaceId, name, itemId } = event.currentTarget.dataset
    if (googlePlaceId && name && itemId) {
      onHighlightClick(googlePlaceId, name, Number(itemId))
    }
  }, [onHighlightClick])

  const hotelHighlightHandlers = useMemo(() => {
    const handlers: Record<number, () => void> = {}
    for (const item of items) {
      if (item.isHighlight && !item.itemPlaceDetail?.placeId && item.savedItemData) {
        handlers[item.id] = () => onHotelHighlightClick(item)
      }
    }
    return handlers
  }, [items, onHotelHighlightClick])

  const highlights = useMemo(() => items.filter((item) => item.isHighlight), [items])
  const hasItems = items.length > 0

  return (
    <DayCardContainer>
      <Group direction="vertical" gap={12}>
        <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="start" fullWidth>
          <Group direction="vertical" gap={4}>
            <Clickable onClick={handleViewDayItineraryClick}>
              <BodyText variant="large" weight="bold">
                {dayLabel}
              </BodyText>
              <Heading variant="heading4">{day.title}</Heading>
            </Clickable>

            {day.description && (
              <BodyText variant="medium">{day.description}</BodyText>
            )}
          </Group>
          <Group direction="horizontal" gap={4}>
            <AskAiChip
              size="medium"
              variant="outline"
              startIcon={<AISparkleIcon size="S" />}
              onClick={handleAskAIClick}
            >
              {askAILabel}
            </AskAiChip>
            <ChatItemDropdown onDelete={handleDelete} />
          </Group>
        </Group>
        {!hasItems && (
          <BodyText variant="medium" colour="neutral-three">
            {emptyDayMessage}
          </BodyText>
        )}
        {highlights.length > 0 && (
          <Group direction="vertical" gap={8} fullWidth>
            <BodyText variant="medium" weight="bold">
              {highlightsHeading}
            </BodyText>
            {highlights.map((item) => (
              <Caption variant="large" key={item.id}>
                <Group direction="horizontal" gap={8} verticalAlign="start">
                  <span>{item.emoji}</span>
                  <span>
                    {item.itemPlaceDetail?.placeId && (
                      <ClickableHighlightTitle
                        data-google-place-id={item.itemPlaceDetail.placeId}
                        data-name={item.name}
                        data-item-id={item.id}
                        onClick={handleHighlightClick}
                      >
                        {item.name}
                      </ClickableHighlightTitle>
                    )}
                    {!item.itemPlaceDetail?.placeId && hotelHighlightHandlers[item.id] && (
                      <ClickableHighlightTitle onClick={hotelHighlightHandlers[item.id]}>
                        {item.name}
                      </ClickableHighlightTitle>
                    )}
                    {!item.itemPlaceDetail?.placeId && !hotelHighlightHandlers[item.id] && <b>{item.name}</b>}
                    {formatDashSeparator(intl)}{item.description}
                  </span>
                </Group>
              </Caption>
            ))}
          </Group>
        )}
        {hasItems && (
          <div>
            <StyledTextButton size="medium" kind="tertiary" onClick={handleViewDayItineraryClick}>
              {viewEntireDayLabel}
            </StyledTextButton>
          </div>
        )}
      </Group>
    </DayCardContainer>
  )
}

export default ItineraryContent
